import type { MessageType } from '~/types/code/code';

type State = {
  /** アラートモーダル用の設定 */
  alert: {
    /** モーダルが開いているかどうか */
    isOpen: boolean;
    /** モーダルに表示するメッセージ */
    message: string;
    /** 警告メッセージかどうか */
    messageType: MessageType;
    /** モーダルを閉じる際に発火させる処理 */
    onClose?: () => void;
  };
  /** 確認モーダル用の設定 */
  confirm: {
    /** モーダルが開いているかどうか */
    isOpen: boolean;
    /** モーダルに表示するメッセージ */
    message: string;
    /** 警告メッセージかどうか */
    isWarning: boolean;
    /** OKを選択した際に発火させる処理 */
    onAccept?: () => void;
    /** キャンセルを選択した際に発火させる処理 */
    onCancel?: () => void;
    /** OKボタンの表示名 */
    acceptButtonText?: string;
    /** キャンセルボタンの表示名 */
    cancelButtonText?: string;
  };
  /** エラーモーダル用の設定 */
  error: {
    /** モーダルが開いているかどうか */
    isOpen: boolean;
    /** 画面ID */
    displayId: string;
    /** エラーコード */
    errorCode: string;
    /** モーダルに表示するメッセージ */
    message: string;
    /** モーダルを閉じる際に発火させる処理 */
    onClose?: () => void;
  };
};

export const useCommonModalStore = defineStore('commonModal', {
  state: (): State => ({
    alert: {
      isOpen: false,
      message: '',
      messageType: '3',
      onClose: undefined,
    },
    confirm: {
      isOpen: false,
      message: '',
      isWarning: false,
      onAccept: undefined,
      onCancel: undefined,
      acceptButtonText: undefined,
      cancelButtonText: undefined,
    },
    error: {
      isOpen: false,
      displayId: '',
      errorCode: '',
      message: '',
      onClose: undefined,
    },
  }),
  actions: {
    /**
     * アラートモーダルを表示する
     *
     * @param message メッセージ
     * @param isWarning 警告メッセージかどうか（未指定またはfalseの場合は確認メッセージ）
     * @param onClose モーダルを閉じる際に発火させる処理
     */
    showAlert(message: string, messageType: MessageType | null | undefined, onClose?: () => void) {
      this.alert.isOpen = true;
      this.alert.message = message;
      this.alert.messageType = messageType ?? '3';
      this.alert.onClose = onClose;
    },
    /**
     * 確認モーダルを表示する
     *
     * @param message メッセージ
     * @param isWarning 警告メッセージかどうか（未指定またはfalseの場合は確認メッセージ）
     * @param onAccept OKを選択した際に発火させる処理
     * @param onCancel キャンセルを選択した際に発火させる処理
     */
    showConfirm(
      message: string,
      isWarning?: boolean,
      onAccept?: () => void,
      onCancel?: () => void,
      acceptButtonText?: string,
      cancelButtonText?: string,
    ) {
      this.confirm.isOpen = true;
      this.confirm.message = message;
      this.confirm.isWarning = isWarning ?? false;
      this.confirm.onAccept = onAccept;
      this.confirm.onCancel = onCancel;
      this.confirm.acceptButtonText = acceptButtonText;
      this.confirm.cancelButtonText = cancelButtonText;
    },
    /**
     * エラーモーダルを表示する
     *
     * @param displayId 画面ID
     * @param errorCode エラーコード
     * @param message エラーメッセージ
     * @param onClose モーダルを閉じる際に発火させる処理
     */
    showError(displayId: string, errorCode: string, message: string, onClose?: () => void) {
      this.error.isOpen = true;
      this.error.displayId = displayId;
      this.error.errorCode = errorCode;
      this.error.message = message;
      this.error.onClose = onClose;
    },
  },
});
